import { Divider } from '@utilitywarehouse/hearth-react';
import { ComponentType, ReactNode } from 'react';

import { DialogID, useDialog } from '@components/Dialog';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useDetailsContext } from '@providers/basket/DetailsProvider/Context';
import { useLocalSessionAddressContext } from '@providers/basket/LocalSessionAddressProvider/Context';
import { clearFormPersistStorage } from '@utils/storagePropertyManagement';

import { Footer } from './Footer';
import { Header, HeaderProps } from './Header';
import { StartAgainDialog } from './Header/StartAgainDialog';
import './styles.scss';

interface LayoutProps {
  title?: string;
  subTitle?: string;
  HeaderComponent?: ComponentType<HeaderProps>;
  children: ReactNode;
  onBack?(): void;
  hideBackButton?: boolean;
  backButtonLabel?: string;
  isErrorBoundary?: boolean;
  isAddressPage?: boolean;
  hideSignupStartAgainDialog?: boolean;
  invertTitle?: boolean;
}

const SignupStartAgainDialog = () => {
  const { handleStartAgain } = useBasketContext();
  const startAgainDialog = useDialog(DialogID.START_AGAIN);
  const { appointment, endAppointment } = useAppointmentContext();
  const { resetDetails } = useDetailsContext();
  const { resetAddressStorage } = useLocalSessionAddressContext();

  return (
    <StartAgainDialog
      id={startAgainDialog.id}
      onExit={async () => {
        if (appointment) {
          await endAppointment();
        } else {
          resetDetails();
          resetAddressStorage();
          clearFormPersistStorage();
          handleStartAgain();
        }
      }}
    />
  );
};

const Layout = ({
  title,
  subTitle,
  HeaderComponent,
  onBack,
  hideBackButton,
  backButtonLabel = 'Back',
  children,
  isErrorBoundary = false,
  isAddressPage = false,
  hideSignupStartAgainDialog = false,
  invertTitle = false,
}: LayoutProps) => {
  const { containerRef } = useBasketContext();

  const HeaderToRender = HeaderComponent ?? Header;

  const headerProps: HeaderProps = {
    title,
    subTitle,
    onBack,
    hideBackButton,
    backButtonLabel,
    isErrorBoundary,
    isAddressPage,
    invertTitle,
  };

  return (
    <div data-testid="layout-container" className="layout__container">
      <HeaderToRender {...headerProps} />
      <div className="layout__content" ref={containerRef}>
        {children}
      </div>
      <Divider />
      <Footer />
      {!hideSignupStartAgainDialog && <SignupStartAgainDialog />}
    </div>
  );
};

export type { LayoutProps };
export { Layout };
